import { api } from './api';
import { format, subDays, subHours, subMonths } from 'date-fns';
import type {
  AnalyticsDashboard,
  AnalyticsFilters,
  TimeSeriesData,
  MetricType,
  TimePeriod,
  TimeGranularity,
  MetricsRequest,
  MetricsResponse,
  ExportOptions,
  GeneratedReport,
  ReportConfig,
  SystemHealthAnalytics,
  CommunicationAnalytics,
  PerformanceMetrics
} from '../types/analytics';

/**
 * Analytics Service
 * 
 * Handles all analytics-related API calls including:
 * - Dashboard metrics and KPIs
 * - Time-series data for charts
 * - System health monitoring
 * - Report generation and export
 * - Real-time analytics updates
 */
export class AnalyticsService {
  private static instance: AnalyticsService;
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  private readonly CACHE_TTL = {
    dashboard: 5 * 60 * 1000, // 5 minutes
    metrics: 2 * 60 * 1000,   // 2 minutes
    health: 30 * 1000,        // 30 seconds
  };

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  // ============ DASHBOARD ANALYTICS ============

  /**
   * Get complete analytics dashboard data
   */
  async getDashboard(filters: AnalyticsFilters): Promise<AnalyticsDashboard> {
    const cacheKey = `dashboard_${JSON.stringify(filters)}`;
    
    // Check cache first
    const cached = this.getCachedData(cacheKey, this.CACHE_TTL.dashboard);
    if (cached) {
      return cached;
    }

    try {
      // For now, we'll use existing communication endpoints and transform the data
      // In a real implementation, there would be dedicated analytics endpoints
      const [communicationData, healthData] = await Promise.all([
        this.getCommunicationAnalytics(filters),
        this.getSystemHealthAnalytics()
      ]);

      const dashboard: AnalyticsDashboard = {
        communication: communicationData,
        systemHealth: healthData,
        lastUpdated: new Date().toISOString(),
        updateFrequency: 30 // 30 seconds
      };

      this.setCachedData(cacheKey, dashboard, this.CACHE_TTL.dashboard);
      return dashboard;
    } catch (error) {
      console.error('Failed to fetch dashboard analytics:', error);
      throw new Error('Unable to load analytics dashboard');
    }
  }

  /**
   * Get communication analytics data
   */
  async getCommunicationAnalytics(filters: AnalyticsFilters): Promise<CommunicationAnalytics> {
    const dateRange = this.calculateDateRange(filters.period);
    
    try {
      // Use existing communication stats endpoint
      const response = await api.get<any>('/communications/stats', {
        params: {
          startDate: dateRange.start,
          endDate: dateRange.end,
          ...filters
        }
      });

      // Transform communication stats to analytics format
      return this.transformCommunicationStats(response.data, filters);
    } catch (error) {
      console.error('Failed to fetch communication analytics:', error);
      // Return mock data for development
      return this.getMockCommunicationAnalytics(filters);
    }
  }

  /**
   * Get system health analytics
   */
  async getSystemHealthAnalytics(): Promise<SystemHealthAnalytics> {
    const cacheKey = 'system_health';
    
    // Check cache first
    const cached = this.getCachedData(cacheKey, this.CACHE_TTL.health);
    if (cached) {
      return cached;
    }

    try {
      // This would be a real health endpoint in production
      const response = await api.get<any>('/system/health');
      const healthData = this.transformSystemHealthData(response.data);
      
      this.setCachedData(cacheKey, healthData, this.CACHE_TTL.health);
      return healthData;
    } catch (error) {
      console.error('Failed to fetch system health:', error);
      // Return mock data for development
      return this.getMockSystemHealthAnalytics();
    }
  }

  // ============ TIME-SERIES METRICS ============

  /**
   * Get time-series data for a specific metric
   */
  async getMetricTimeSeries(
    metric: MetricType,
    period: TimePeriod,
    granularity: TimeGranularity,
    filters?: Partial<AnalyticsFilters>
  ): Promise<TimeSeriesData> {
    const cacheKey = `metric_${metric}_${period}_${granularity}_${JSON.stringify(filters)}`;
    
    // Check cache first
    const cached = this.getCachedData(cacheKey, this.CACHE_TTL.metrics);
    if (cached) {
      return cached;
    }

    try {
      const dateRange = this.calculateDateRange(period);
      const request: MetricsRequest = {
        metric,
        period,
        granularity,
        filters: {
          ...filters,
          startDate: dateRange.start,
          endDate: dateRange.end
        }
      };

      // This would be a dedicated metrics endpoint
      const response = await api.post<MetricsResponse>('/analytics/metrics', request);

      this.setCachedData(cacheKey, response.data, this.CACHE_TTL.metrics);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch metric ${metric}:`, error);
      // Return mock data for development
      return this.getMockTimeSeriesData(metric, period, granularity);
    }
  }

  /**
   * Get multiple metrics in parallel
   */
  async getMultipleMetrics(
    metrics: MetricType[],
    period: TimePeriod,
    granularity: TimeGranularity,
    filters?: Partial<AnalyticsFilters>
  ): Promise<Record<MetricType, TimeSeriesData>> {
    const promises = metrics.map(metric => 
      this.getMetricTimeSeries(metric, period, granularity, filters)
        .then(data => ({ metric, data }))
        .catch(error => ({ metric, error, data: null }))
    );

    const results = await Promise.all(promises);
    const metricsData: Record<string, TimeSeriesData> = {};

    results.forEach((result) => {
      if ('data' in result && result.data) {
        metricsData[result.metric] = result.data;
      } else {
        console.error(`Failed to load metric ${result.metric}:`, 'error' in result ? result.error : 'Unknown error');
        // Provide fallback mock data
        metricsData[result.metric] = this.getMockTimeSeriesData(result.metric, period, granularity);
      }
    });

    return metricsData as Record<MetricType, TimeSeriesData>;
  }

  // ============ REPORT GENERATION ============

  /**
   * Generate a custom analytics report
   */
  async generateReport(config: ReportConfig): Promise<GeneratedReport> {
    try {
      const response = await api.post<GeneratedReport>(
        '/analytics/reports',
        config
      );
      return response;
    } catch (error) {
      console.error('Failed to generate report:', error);
      throw new Error('Unable to generate analytics report');
    }
  }

  /**
   * Export analytics data in various formats
   */
  async exportData(options: ExportOptions): Promise<{ url: string; filename: string }> {
    try {
      const response = await api.post<{ url: string; filename: string }>(
        '/analytics/export',
        options
      );
      return response;
    } catch (error) {
      console.error('Failed to export analytics data:', error);
      throw new Error('Unable to export analytics data');
    }
  }

  // ============ PERFORMANCE MONITORING ============

  /**
   * Track analytics performance metrics
   */
  trackPerformance(metrics: Partial<PerformanceMetrics>): void {
    const performanceData = {
      timestamp: new Date().toISOString(),
      ...metrics
    };

    // In a real implementation, this would send to a monitoring service
    console.log('Analytics Performance:', performanceData);
  }

  // ============ UTILITY METHODS ============

  /**
   * Calculate date range for a given period
   */
  private calculateDateRange(period: TimePeriod): { start: string; end: string } {
    const end = new Date();
    let start: Date;

    switch (period) {
      case '1h':
        start = subHours(end, 1);
        break;
      case '24h':
        start = subHours(end, 24);
        break;
      case '7d':
        start = subDays(end, 7);
        break;
      case '30d':
        start = subDays(end, 30);
        break;
      case '90d':
        start = subDays(end, 90);
        break;
      case '1y':
        start = subMonths(end, 12);
        break;
      default:
        start = subDays(end, 7);
    }

    return {
      start: format(start, 'yyyy-MM-dd\'T\'HH:mm:ss\'Z\''),
      end: format(end, 'yyyy-MM-dd\'T\'HH:mm:ss\'Z\'')
    };
  }

  /**
   * Cache management
   */
  private getCachedData(key: string, ttl: number): any | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < ttl) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  private setCachedData(key: string, data: any, ttl: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    this.cache.clear();
  }

  // ============ MOCK DATA GENERATORS (for development) ============

  private getMockCommunicationAnalytics(filters: AnalyticsFilters): CommunicationAnalytics {
    const mockMetrics = {
      totalSent: 15420,
      totalDelivered: 14683,
      totalBounced: 234,
      totalOpened: 8952,
      totalClicked: 2847,
      totalUnsubscribed: 67,
      deliveryRate: 95.2,
      openRate: 61.0,
      clickRate: 31.8,
      bounceRate: 1.5,
      unsubscribeRate: 0.4
    };

    return {
      period: filters.period,
      metrics: mockMetrics,
      timeSeries: {
        sends: this.getMockTimeSeriesData('send_volume', filters.period, 'hour'),
        deliveries: this.getMockTimeSeriesData('delivery_rate', filters.period, 'hour'),
        opens: this.getMockTimeSeriesData('open_rate', filters.period, 'hour'),
        clicks: this.getMockTimeSeriesData('click_rate', filters.period, 'hour'),
        bounces: this.getMockTimeSeriesData('bounce_rate', filters.period, 'hour'),
      },
      breakdown: {
        byChannel: [
          { channel: 'email', metrics: { ...mockMetrics, totalSent: 12420 } },
          { channel: 'sms', metrics: { ...mockMetrics, totalSent: 2500, openRate: 98.0 } },
          { channel: 'push', metrics: { ...mockMetrics, totalSent: 500, openRate: 45.0 } },
        ],
        byTemplate: [
          { templateId: '1', templateName: 'Welcome Email', metrics: mockMetrics },
          { templateId: '2', templateName: 'Event Reminder', metrics: mockMetrics },
        ],
        byRecipientGroup: [
          { groupName: 'All Members', metrics: mockMetrics },
          { groupName: 'Organizers', metrics: { ...mockMetrics, openRate: 85.0 } },
        ]
      }
    };
  }

  private getMockSystemHealthAnalytics(): SystemHealthAnalytics {
    return {
      current: {
        uptime: 99.8,
        responseTime: 145,
        errorRate: 0.2,
        queueDepth: 12,
        throughput: 450,
        memoryUsage: 68.5,
        cpuUsage: 34.2
      },
      timeSeries: {
        uptime: this.getMockTimeSeriesData('system_health', '24h', 'hour'),
        responseTime: this.getMockTimeSeriesData('response_time', '24h', 'hour'),
        errorRate: this.getMockTimeSeriesData('error_rate', '24h', 'hour'),
        throughput: this.getMockTimeSeriesData('send_volume', '24h', 'hour')
      },
      alerts: [
        {
          id: '1',
          severity: 'medium',
          message: 'Queue depth approaching threshold',
          timestamp: new Date().toISOString(),
          resolved: false
        }
      ],
      services: [
        { name: 'Email Service', status: 'healthy', responseTime: 125, lastChecked: new Date().toISOString() },
        { name: 'SMS Service', status: 'healthy', responseTime: 89, lastChecked: new Date().toISOString() },
        { name: 'Database', status: 'healthy', responseTime: 23, lastChecked: new Date().toISOString() },
      ]
    };
  }

  private getMockTimeSeriesData(metric: MetricType, period: TimePeriod, granularity: TimeGranularity): TimeSeriesData {
    const now = new Date();
    const dataPoints = [];
    const numPoints = this.getDataPointCount(period, granularity);

    for (let i = numPoints - 1; i >= 0; i--) {
      const timestamp = new Date(now.getTime() - (i * this.getIntervalMs(granularity)));
      const baseValue = this.getBaseValue(metric);
      const variance = baseValue * 0.2; // 20% variance
      const value = baseValue + (Math.random() - 0.5) * variance;

      dataPoints.push({
        timestamp: timestamp.toISOString(),
        value: Math.max(0, Math.round(value * 100) / 100),
        label: format(timestamp, this.getDateFormat(granularity))
      });
    }

    const values = dataPoints.map(p => p.value);
    const total = values.reduce((sum, val) => sum + val, 0);
    const average = total / values.length;
    const peak = Math.max(...values);
    const firstValue = values[0] || 0;
    const lastValue = values[values.length - 1] || 0;
    const trend = lastValue > firstValue ? 'up' : 'down';
    const changePercent = firstValue !== 0 ? ((lastValue - firstValue) / firstValue) * 100 : 0;

    return {
      metric,
      period,
      granularity,
      dataPoints,
      summary: {
        total,
        average,
        peak,
        trend,
        changePercent: Math.round(changePercent * 100) / 100
      }
    };
  }

  private getDataPointCount(period: TimePeriod, granularity: TimeGranularity): number {
    const counts = {
      '1h': { minute: 60, hour: 1 },
      '24h': { hour: 24, day: 1 },
      '7d': { hour: 168, day: 7 },
      '30d': { day: 30, week: 4 },
      '90d': { day: 90, week: 12 },
      '1y': { week: 52, month: 12 }
    };
    
    const periodCounts = counts[period];
    if (!periodCounts) return 24;
    return (periodCounts as any)[granularity] || 24;
  }

  private getIntervalMs(granularity: TimeGranularity): number {
    const intervals = {
      minute: 60 * 1000,
      hour: 60 * 60 * 1000,
      day: 24 * 60 * 60 * 1000,
      week: 7 * 24 * 60 * 60 * 1000,
      month: 30 * 24 * 60 * 60 * 1000
    };
    return intervals[granularity];
  }

  private getBaseValue(metric: MetricType): number {
    const baseValues = {
      delivery_rate: 95,
      open_rate: 62,
      click_rate: 18,
      bounce_rate: 2,
      unsubscribe_rate: 0.5,
      send_volume: 1000,
      response_time: 150,
      error_rate: 0.3,
      system_health: 99
    };
    return baseValues[metric] || 50;
  }

  private getDateFormat(granularity: TimeGranularity): string {
    const formats = {
      minute: 'HH:mm',
      hour: 'HH:mm',
      day: 'MM/dd',
      week: 'MM/dd',
      month: 'MMM'
    };
    return formats[granularity];
  }

  private transformCommunicationStats(_data: any, filters: AnalyticsFilters): CommunicationAnalytics {
    // Transform actual backend data to our analytics format
    // This is a placeholder - real implementation would map the actual API response
    return this.getMockCommunicationAnalytics(filters);
  }

  private transformSystemHealthData(_data: any): SystemHealthAnalytics {
    // Transform actual backend health data to our analytics format
    // This is a placeholder - real implementation would map the actual API response
    return this.getMockSystemHealthAnalytics();
  }
}

// Export singleton instance
export const analyticsService = AnalyticsService.getInstance();